import React from "react";
import TextField from "../lib/mdb/text-field";
import _set from "lodash/set";
import _unset from "lodash/unset";
import {cashRequiresCheckNumber, cashRequiresName, isWalletPM, updateTotals} from "./payment-utils";
import _get from "lodash/get";
import {mapMessages} from "./utils";


/**
 * This function helps with resetting the fields
 * @param state
 * @param paymentMethodCategory
 */
function resetPaymentMethodFields(state, paymentMethodCategory) {
    const {data, config} = state,
        {paymentConfig} = config,
        {currentUserWallet} = data,
        {paymentMethods} = paymentConfig;
    if (isWalletPM(paymentMethodCategory)) {
        let wallet = findWallet(currentUserWallet, paymentMethodCategory);
        _set(data, 'paymentMethod.token', wallet.token);
        _set(data, 'paymentMethod.type', wallet.type);
        _set(data, 'paymentMethod.walletCategory',paymentMethods.find((pm)=>(pm.code===wallet.type)).categoryCode);
        _set(data, "paymentMethod.accountNumber", wallet.accountNumber)
    } else {
        _set(data, 'paymentMethod.token', '');
        _set(data, 'paymentMethod.type', '');
    }


    const fields = FieldsConfig[paymentMethodCategory];
    if (fields) {
        for (let field of fields) {
            _set(data, field, '');
            state.resetError(field);
        }
        // If category has a single item, set the type now
        const pmList = paymentMethods.filter(pm => pm.categoryCode === paymentMethodCategory);
        const pmType = (pmList.length === 1 ? pmList[0].code : '');
        _set(data, 'paymentMethod.type', pmType);
        if (paymentMethodCategory === 'DD') {
            // for eCheck preselect the first type
            _set(data, 'paymentMethod.type', pmList[0].code);
        }
    }
    _unset(data, 'paymentMethod.callbackNumber');
    _unset(data, 'paymentMethod.callbackNumberExt');
    _unset(data, 'paymentMethod.doCallback');

    // trigger recalculation of the conv fee and totals
    // FIXME: Use better event handling
    for (let payment of data.payments) {
        payment.header.paymentAmount = payment.header.paymentAmount + 0;
    }
    updateTotals(data);

    // revalidate email field because for certain billers with certain payment methods,
    // email is required, only revalidate if error exists (to fix scenario if email was
    // required for one payment method but not another)
    const emailField = 'customer.email',
        email = _get(data, emailField);
    if(state.validationState[emailField]){
        state.validationState[emailField] = state.validateField(emailField, email);
    }
}

/**
 * This method takes the selected wallet value from the dropdown,
 * such as: WALLET-0, WALLET-1, etc, and returns the
 * corresponding wallet object.
 *
 * @param selected
 */
function findWallet(currentUserWallet, selected) {
    let index = getWalletIndex(selected);
    return currentUserWallet[index];
}


/**
 * Returns the index for a wallet PM.
 *   For WALLET-1, returns 1
 *   For WALLET-2, returns 2
 *   For WALLET-5, returns 5
 *
 * @param pmType
 */
function getWalletIndex(pmType) {
    return pmType.match(/([0-9]+)/)[0];
}

function preparePaymentMethod(paymentMethod) {
    let {paymentMethodCategoryType, type} = paymentMethod;
    let pm = {};

    if (paymentMethodCategoryType === "CC" || paymentMethodCategoryType === "DC") {
        const {creditCardExpiryDate} = paymentMethod;
        pm = {
            type: type,
            "account-number": paymentMethod.accountNumber,
            "credit-card-expiry-date": {
                month: (creditCardExpiryDate || '').split("/")[0],
                year: "20" + (creditCardExpiryDate || '').split("/")[1]
            },
            cvv: paymentMethod.cvv,
            "card-holder-name": paymentMethod.cardHolderName,
        };
    } else if (paymentMethodCategoryType === "DD") {
        pm = {
            type: type,
            "routing-number": paymentMethod.routingNumber,
            "account-number": paymentMethod.accountNumber,
            "fi-name": paymentMethod.fiName,
            "card-holder-name": paymentMethod.cardHolderName
        };
    } else if (paymentMethodCategoryType === 'PD') {
        pm = {
            type: type,
            "account-number": paymentMethod.accountNumber,
            "card-holder-name": paymentMethod.cardHolderName
        };
    } else if (isWalletPM(paymentMethodCategoryType)) {
        pm = {
            token: paymentMethod.token
        };
        paymentMethodCategoryType = paymentMethod.walletCategory
    } else if (paymentMethodCategoryType === 'LAST_USED_PM') {
        // FIXME: add last used payment method here
    } else if (paymentMethodCategoryType && paymentMethodCategoryType.endsWith(STAGED_PM_CATEGORY_SUFFIX)) {
        console.log(`Stripping suffix from staged payment method category: ${paymentMethodCategoryType}`);
        paymentMethodCategoryType = paymentMethodCategoryType.substring(0, paymentMethodCategoryType.indexOf(STAGED_PM_CATEGORY_SUFFIX));
    } else if (paymentMethodCategoryType === 'CASH') {
        pm = {
            type: type
        };
        if (cashRequiresCheckNumber(type)) {
            pm["check-number"] = paymentMethod.checkNumber;
        }
        if (cashRequiresName(type)) {
            pm["card-holder-name"] = paymentMethod.cardHolderName;
        }
    } else if (paymentMethodCategoryType === 'CP_POS') {
        pm = {
            type: type,
            "account-number": paymentMethod.accountNumber,
            "card-present-manual-entry": paymentMethod.cardPresentManualEntry,
            "card-present-terminal-hsn": paymentMethod.cardPresentTerminalHSN
        };
    } else {
        console.log(`Unknown payment method category: ${paymentMethodCategoryType}`);
    }

    pm["save-for-reuse"] = !!paymentMethod.saveForReuse;
    return {pm, paymentMethodCategoryType};
}

/**
 * This method prepares the wallet JSON to hit the XOTP /profiles API
 * @param data
 * @returns {{"payment-method", "do-not-save": boolean, "user-info": {"login-id": *}, customer: {address: {city: *, state: *, "zip-code": *, line2: *, line1: *}, "day-phone-nr": (string|number), "day-phone-ext": (string), "middle-name": *, email: *, "first-name": *, "last-name": *}}}
 */
function prepareWalletForSubmission(data) {

    const {currentCustomer, paymentMethod, cpDoNotSavePmEnabled} = data,
        {address} = currentCustomer;

    let {pm} = preparePaymentMethod(paymentMethod);
    return {
        "user-info" : {
            "login-id": currentCustomer.loginId
        },
        "customer": {
            "email": currentCustomer.email,
            "first-name": currentCustomer.firstName,
            "last-name": currentCustomer.lastName,
            "middle-name": currentCustomer.middleName,
            "day-phone-nr": currentCustomer.dayPhoneNr,
            "day-phone-ext": currentCustomer.dayPhoneExt,
            "address": {
                "zip-code": address.zipCode,
                "line1": address.line1,
                "line2": address.line2,
                "city": address.city,
                "state": address.state
            }
        },
        "payment-method": pm,
        "do-not-save": !cpDoNotSavePmEnabled
    }
}

/**
 * This method create wallet entry from response to add to wallet
 * @param profileResponse
 * @returns {*}
 */
function createWalletEntryFromResponse(profileResponse) {
    // In-case someone wants to reformat the response to a certain format
    return profileResponse
}

const initPopover = (linkId, contentId) => {
    let $template = '<div class="popover-opp" role="tooltip"><div class="arrow"></div><h3 class="popover-header"></h3><div class="popover-body"></div></div>';
    $('#' + linkId).popover({
        template: $template,
        html: true,
        content: function () {
            let contentEl = $('#' + contentId).clone();
            return contentEl.children().first();
        }
    });
};

const mapCardTypeToCss = (code) => {
    const item = CARD_TYPE_MAP.find(item => item.code === code);
    return item && item.cssClass;
};

const CARD_TYPE_MAP = [
    {code: "VISA_DEBIT", cssClass: "methodVisaDebit"},
    {code: "MC_DEBIT", cssClass: "methodMastercardDebit"},
    {code: "VISA", cssClass: "methodVisa"},
    {code: "MC", cssClass: "methodMastercard"},
    {code: "AMEX", cssClass: "methodAmex"},
    {code: "DISC", cssClass: "methodDiscover"},
    {code: "DISC_DEBIT", cssClass: "methodDiscoverDebit"}
];

const CardHolderName = ({messages}) => {
    return (
        <TextField
            name="paymentMethod.cardHolderName"
            placeholder={messages["opp.cardHolderName.tip"]}
            description={messages["opp.cardHolderName.label"]}
            maxLength="256"
            className="md-form form-group"
            labelClass="bootstrap-label"
            aria-required="true"
            validations={{
                required: messages["pmCardHolderName.empty"],
                name: messages["pmCardHolderName.invalid"] || "Card holder name is invalid"
            }}
            autoComplete="off"/>
    );
};

const CvvPopover = ({config, isCreditCard}) => {
    const {paymentConfig, messages} = config,
        amexPm = paymentConfig.paymentMethods.find(pm => (pm.code === "AMEX")),
        visaCssClass = amexPm ? "col-md-6" : "col-12 mt-3";
    return (
        <div className="modal" key="popover-cvv" id="popover-cvv" tabIndex="-1" aria-hidden="true">
            <div className="row">
                <div className={visaCssClass}><img
                    src={messages["opp.image_visa_mc_disc"] || "images/image_visa_mc_disc.gif"} width="214"
                    height="157"/><br/><br/>
                    <strong>{messages["visaAndMcUsers.title"]}</strong><br/>{messages["visaAndMcUsers.desc"]}
                </div>
                {amexPm && isCreditCard && (
                    <div className="col-md-6 mt-3 mt-sm-0"><img
                        src={messages["opp.image_amex"] || "images/image_amex.gif"}
                        width="214" height="157"/><br/><br/>
                        <strong>{messages["amexCardUsers.title"]}</strong><br/>{messages["amexCardUsers.desc"]}
                    </div>
                )}
                <div className="col-12 mt-3">
                    <strong>{messages["whatIsCvv.title"]}</strong><br/>{messages["whatIsCvv.desc"]}<br/><br/>
                    <strong>{messages["mustEnterCvv.title"]}</strong><br/>{messages["mustEnterCvv.desc"]}
                </div>
            </div>
        </div>
    );
};

const FieldsConfig = {
    CC: ['paymentMethod.type', 'paymentMethod.accountNumber', 'paymentMethod.cvv', 'paymentMethod.creditCardExpiryDate', 'paymentMethod.cardHolderName'],
    DC: ['paymentMethod.type', 'paymentMethod.accountNumber', 'paymentMethod.cvv', 'paymentMethod.creditCardExpiryDate', 'paymentMethod.cardHolderName'],
    DD: ['paymentMethod.type', 'paymentMethod.accountNumber', 'paymentMethod.fiName', 'paymentMethod.routingNumber', 'paymentMethod.cardHolderName', 'paymentMethod.acceptDirectPaymentAgreement'],
    PD: ['paymentMethod.type', 'paymentMethod.accountNumber', 'paymentMethod.cardHolderName'],
    CASH: ['paymentMethod.type'],
    CP_POS: ['paymentMethod.type'],
    WALLET: ['paymentMethod.token'],
    CURRENT: ['paymentMethod.accountNumber', 'paymentMethod.token']
};

const StaticAchAgreement = ({config}) => {
    const {messages} = config,
        msgList = mapMessages(messages, "opp.achAgreement.msg", 5);
    return (
        <div className="modal mdb-modal fade" id="modal-ach-agreement" tabIndex="-1" role="dialog"
             aria-labelledby="modal-ach-agreement" aria-hidden="true">
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="modal-ach-agreement">
                            {messages["opp.achAgreement.title"]}
                        </h5>
                        <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true"><i className="material-icons">&#xE5CD;</i></span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="row">
                            <div className="col-12">
                                {msgList.map((msg, index) => {
                                    if (index === 0) return (
                                        <p key={"ach-msg-" + index}><b key={"ach-msgb-" + index}>{msg}</b></p>);
                                    return (<p key={"ach-msg-" + index}>{msg}</p>);
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn btn-primary" data-dismiss="modal">
                            {messages["closeButton.label"]}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export {
    // Functions here
    preparePaymentMethod,
    prepareWalletForSubmission,
    createWalletEntryFromResponse,
    resetPaymentMethodFields,
    findWallet,
    getWalletIndex,

    // Const here
    initPopover,
    mapCardTypeToCss,
    CardHolderName,
    CvvPopover,
    StaticAchAgreement,
    FieldsConfig,
}